import moment from 'moment';
import { IHeadCell } from '../../../../shared/interfaces/table-display.model';
import { TableActionButtons } from '../../miscellaneous-components/table-action-buttons';
import { FunctionComponent, SetStateAction } from 'react';
import { EditUploadLineEmissionDialog } from '../../../dialogs/settings/data-import/edit-upload-line-emission-dialog';
import { TableDisplay } from '../../miscellaneous-components/table-display';
import { IEditFileUploadLine, IFileUploadLine } from '../../../../shared/services/file-upload-all-lines/file-upload-all-lines.model';
import { useNavigate } from 'react-router-dom';
import { useIsReportPeriodLocked } from '../../../../hooks/use-is-report-period-locked';
import { Chip, Tooltip, IconButton, Stack } from '@mui/material';
import { EditOutlined, OpenInNewOutlined } from '@mui/icons-material';
import { IBillDocument } from '../../../../shared/services/file-upload/file-upload.model';
import { buildBillLineCells } from './bill-line-cells';
import { openBillDocument } from '../../../../shared/utils/open-bill-document';

export const CsvUploadEmissions: FunctionComponent<{
  uploadId: string;
  fileUploadLines: IFileUploadLine[],
  editClickHandler: (e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
    uploadLine: IFileUploadLine) => void,
  deleteHandler: (id: string) => void,
  openDialog: boolean,
  setValidatedUploadLines: React.Dispatch<SetStateAction<any>>,
  selected: string[],
  setSelected: React.Dispatch<SetStateAction<any>>,
  editUploadLine: IFileUploadLine | undefined,
  closeDialogHandler: () => void,
  submitHandler: (lineId: string, editUploadLine: IEditFileUploadLine) => void,
  inputPage: number,
  setInputPage: React.Dispatch<SetStateAction<any>>,
  totalRecords: number,
  perPage: number,
  setPerPage: React.Dispatch<SetStateAction<any>>,
  status: string,
  setOrder: React.Dispatch<SetStateAction<any>>,
  setOrderBy: React.Dispatch<SetStateAction<any>>,
  isSubmitting: boolean,
  selectAllFiltered?: boolean,
  onExitSelectAllFiltered?: (remainingIdsOnPage: string[]) => void,
  /**
   * A bill upload's documents. When given, the table shows each row's bill and what was read off it
   * (Bill, Activity, Usage, Period, Status, Notes) instead of the spreadsheet error columns, whose
   * row numbers mean nothing for bills.
   */
  billDocuments?: IBillDocument[],
}> = ({
  uploadId,
  fileUploadLines,
  editClickHandler,
  openDialog,
  setValidatedUploadLines,
  selected,
  setSelected,
  inputPage,
  setInputPage,
  totalRecords,
  perPage,
  setOrder,
  setOrderBy,
  setPerPage,
  editUploadLine,
  closeDialogHandler,
  submitHandler,
  deleteHandler,
  status,
  isSubmitting,
  selectAllFiltered,
  onExitSelectAllFiltered,
  billDocuments }) => {
    const navigate = useNavigate()
    //Hooks
    const isReportPeriodLocked = useIsReportPeriodLocked()
    const showMatchColumn = (fileUploadLines || []).some((line) =>
      Boolean(line.linkStatus) || (Array.isArray(line.matchReasons) && line.matchReasons.length > 0)
    )

    const normaliseValidationErrors = (errors: any): any[] => {
      if (Array.isArray(errors)) return errors
      if (typeof errors === 'string') {
        try {
          const parsed = JSON.parse(errors)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          return []
        }
      }
      if (errors && typeof errors === 'object') {
        return Object.values(errors).flat().filter(Boolean)
      }
      return []
    }

    const mapValidationErrors = (uploadLines: any[]) => {
      return uploadLines.map((uploadLine: any) => {
        const rowNumber = typeof uploadLine.rowNumber === 'number' ? uploadLine.rowNumber : undefined
        const validationErrors = normaliseValidationErrors(uploadLine.validationErrors)
        const reviewErrors = validationErrors.length === 0 && uploadLine.linkStatus === 'REVIEW'
          ? (uploadLine.matchReasons || []).map((reason: string) => ({
            field: 'Emission match',
            message: reason,
            severity: 'warning'
          }))
          : []
        const displayErrors = reviewErrors.length > 0 ? reviewErrors : validationErrors

        const fieldList = displayErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {displayErrors.map((ve: any, idx: number) => (
              <div key={idx}>{ve.field || 'Unknown'}</div>
            ))}
          </Stack>
        ) : ''

        const errorList = displayErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {displayErrors.map((ve: any, idx: number) => {
              const errorText = ve.message || 'Validation failed'
              const truncatedError = errorText.length > 100 ? errorText.substring(0, 100) + '...' : errorText
              return <div key={idx}>{truncatedError}</div>
            })}
          </Stack>
        ) : ''

        const severityList = displayErrors.length > 0 ? (
          <Stack spacing={0.5}>
            {displayErrors.map((ve: any, idx: number) => (
              <Chip
                key={idx}
                color={ve.severity === 'warning' ? 'warning' : 'error'}
                label={ve.severity === 'warning' ? 'Warning' : 'Error'}
                size="small"
                variant="outlined"
              />
            ))}
          </Stack>
        ) : status === 'pending' ? (
          <Chip
            color={uploadLine.commitStatus === 'FAILED' ? 'error' : 'success'}
            label={uploadLine.commitStatus === 'FAILED' ? 'Failed' : 'Ready'}
            size="small"
            variant="outlined"
          />
        ) : ''

        return {
          id: uploadLine.id,
          row: rowNumber || '',
          field: fieldList,
          error: errorList,
          severity: severityList,
          action: (
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
          ),
        }
      })
    }

    const isBillUpload = Array.isArray(billDocuments)
    const billDocumentsById: Record<string, IBillDocument> = {}
    ;(billDocuments || []).forEach((document) => { billDocumentsById[document.id] = document })

    const billHeadCells: IHeadCell[] = status === 'pending'
      ? [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'bill', numeric: false, label: 'Bill', sortable: false },
        { id: 'activity', numeric: false, label: 'Activity', sortable: false },
        { id: 'usage', numeric: false, label: 'Usage', sortable: false },
        { id: 'period', numeric: false, label: 'Period', sortable: false },
        { id: 'status', numeric: false, label: 'Status', sortable: false },
        { id: 'notes', numeric: false, label: 'Notes', sortable: false },
      ]
      : [
        ...(status === 'completed' ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        { id: 'bill', numeric: false, label: 'Bill', sortable: false },
        { id: 'conversion', numeric: false, label: 'Conversion', sortable: false },
        { id: 'unitsConsumed', numeric: true, label: 'Units Consumed', sortable: true },
        { id: 'startDate', numeric: false, label: 'Start Date', sortable: true },
        { id: 'endDate', numeric: false, label: 'End Date', sortable: true },
      ]

    const viewBillButton = (uploadLine: any) => uploadLine.billDocumentId ? (
      <Tooltip title="View bill" placement='top'>
        <IconButton onClick={() => openBillDocument(uploadLine.billDocumentId)} size="small">
          <OpenInNewOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
        </IconButton>
      </Tooltip>
    ) : null

    const mapBillLines = (uploadLines: any[]) => uploadLines.map((uploadLine: any) => {
      const cells = buildBillLineCells(uploadLine, billDocumentsById, 'Ready')
      return {
        id: uploadLine.id,
        ...cells,
        action: (
          <Stack direction='row' spacing={0.5}>
            <Tooltip title="Edit" placement='top'>
              <IconButton
                onClick={() => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
                size="small">
                <EditOutlined sx={{ cursor: 'pointer', fontSize: 18 }} />
              </IconButton>
            </Tooltip>
            {viewBillButton(uploadLine)}
          </Stack>
        ),
      }
    })

    const spreadsheetHeadCells: IHeadCell[] = status === 'pending' ?
      [
        { id: 'action', numeric: false, label: 'Action', sortable: false },
        { id: 'row', numeric: true, label: 'Row', sortable: false },
        { id: 'field', numeric: false, label: 'Field', sortable: false },
        { id: 'error', numeric: false, label: 'Error', sortable: false },
        { id: 'severity', numeric: false, label: 'Severity', sortable: false },
      ]
      :
      [
        ...((status !== 'discarded' && ((status === 'completed') || ((status === 'pending' || status === 'validated') && !isReportPeriodLocked))) ? [{ id: 'action', numeric: false, label: 'Action', sortable: false }] : []),
        ...(showMatchColumn ? [{ id: 'match', numeric: false, label: 'Match', sortable: false }] : []),
        { id: 'conversion', numeric: false, label: 'Conversion', sortable: false },
        { id: 'unitsConsumed', numeric: true, label: 'Units Consumed', sortable: true },
        { id: 'startDate', numeric: false, label: 'Start Date', sortable: true },
        { id: 'endDate', numeric: false, label: 'End Date', sortable: true },
      ];

    const headCells: IHeadCell[] = isBillUpload ? billHeadCells : spreadsheetHeadCells

    // The factor path reads like the emissions table: middot-separated levels, no trailing unit
    // (the unit belongs with the amount). Empty levels are dropped rather than left as commas.
    const factorPath = (parts: (string | null | undefined)[]) =>
      parts.map(p => (p || '').trim()).filter(Boolean).join(' \u00B7 ')
    // Amount with thousands separators and its unit, the way the emissions table shows
    // consumption ("449,177.1014 kWh"), instead of a bare 449177.1014.
    const formatAmount = (value: number | string | null | undefined, unit?: string | null) => {
      const n = Number(value)
      const amount = Number.isFinite(n) ? n.toLocaleString('en-GB', { maximumFractionDigits: 4 }) : (value ?? '')
      return unit ? `${amount} ${unit}` : `${amount}`
    }

    const rows = status === 'pending'
      ? (isBillUpload ? mapBillLines(fileUploadLines || []) : mapValidationErrors(fileUploadLines || []))
      : fileUploadLines.map((uploadLine: IFileUploadLine) => {
        const matchLabel = uploadLine.linkStatus === 'LINKED'
          ? 'Linked'
          : uploadLine.linkStatus === 'UNLINKED'
            ? 'Standalone'
            : uploadLine.linkStatus === 'REVIEW'
              ? 'Needs review'
              : null
        const matchColor = uploadLine.linkStatus === 'LINKED'
          ? 'success'
          : uploadLine.linkStatus === 'REVIEW'
            ? 'warning'
            : 'default'
        return {
          id: uploadLine.id,
          conversion: uploadLine.ruleId && uploadLine.rule
            ? `${uploadLine.ruleName}: ${factorPath([uploadLine.rule.groupName, uploadLine.rule.categoryName, uploadLine.rule.detailLevelTwo, uploadLine.rule.detailLevelThree, uploadLine.rule.detailLevelFour, uploadLine.rule.detailLevelFive])}`
            : factorPath([uploadLine.groupName, uploadLine.categoryName, uploadLine.detailLevelTwo, uploadLine.detailLevelThree, uploadLine.detailLevelFour, uploadLine.detailLevelFive]),
          match: showMatchColumn ? (
            matchLabel ? (
              <Tooltip
                placement='top'
                title={(
                  <Stack spacing={0.5}>
                    {(uploadLine.matchReasons || []).map((reason, index) => (
                      <div key={index}>{reason}</div>
                    ))}
                  </Stack>
                )}
              >
                <Chip size='small' color={matchColor as any} variant='outlined' label={matchLabel} />
              </Tooltip>
            ) : null
          ) : undefined,
          bill: isBillUpload ? buildBillLineCells(uploadLine, billDocumentsById).bill : undefined,
          startDate: moment(uploadLine.startDate).format('DD/MM/YYYY'),
          endDate: moment(uploadLine.endDate).format('DD/MM/YYYY'),
          unitsConsumed: formatAmount(uploadLine.unitsConsumed, uploadLine.measurementUnitName),
          action: status === 'validated' && !isReportPeriodLocked ? (
            <TableActionButtons
              id={uploadLine.id}
              includeEdit={true}
              includeDelete={true}
              onEdit={(_id: string) => editClickHandler({ stopPropagation: () => { } } as any, uploadLine)}
              onDelete={(id: string) => deleteHandler(id)}
            />
          ) : status === 'completed' ? (
            isBillUpload ? (
              <Stack direction='row' spacing={0.5} alignItems='center'>
                <TableActionButtons
                  id={uploadLine.id}
                  includeView={true}
                  onView={() => uploadLine.emissionId ? viewTransactionClickHandler(uploadLine.emissionId) : undefined}
                  disableButton={!uploadLine.emissionId}
                />
                {viewBillButton(uploadLine)}
              </Stack>
            ) : (
              <TableActionButtons
                id={uploadLine.id}
                includeView={true}
                onView={() => uploadLine.emissionId ? viewTransactionClickHandler(uploadLine.emissionId) : undefined}
                disableButton={!uploadLine.emissionId}
              />
            )
          ) : null
        }
      })

    const viewTransactionClickHandler = (transactionId: string) => {
      navigate(`/emissions/overview/${transactionId}?backLink=${encodeURIComponent(`/settings/data-upload/import-history/emissions/${uploadId}?tabId=1`)}&backText=${encodeURIComponent('Back to import')}`)
    }

    const handleSelected = (selectedRowId: string[]) => {
      const formatted = selectedRowId.map(row => {
        return {
          id: row
        }
      })
      setValidatedUploadLines(formatted)
    }

    const handleOrderChange = (orderBy: string, order: 'asc' | 'desc',) => {
      setOrderBy(orderBy)
      setOrder(order)
    };


    return (
      <>
        <TableDisplay
          headCells={headCells}
          rows={rows}
          onOrderChange={handleOrderChange}
          selectByIcon={(status === 'validated' || status === 'pending') && !isReportPeriodLocked ? false : true}
          selectByCheckboxOnly={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          handleSelected={handleSelected}
          selected={selected}
          setSelected={setSelected}
          selectAllFiltered={selectAllFiltered}
          onExitSelectAllFiltered={onExitSelectAllFiltered}
          haveCheckboxSelect={((status === 'validated' || status === 'pending') && !isReportPeriodLocked) ? true : false}
          inputPage={inputPage}
          setInputPage={setInputPage}
          totalRecords={totalRecords}
          perPage={perPage}
          setPerPage={setPerPage} />
        {editUploadLine && openDialog && !isReportPeriodLocked &&
          <EditUploadLineEmissionDialog
            open={openDialog}
            isSubmitting={isSubmitting}
            closeHandler={closeDialogHandler}
            submitHandler={submitHandler}
            editUploadLine={editUploadLine} />}
      </>
    )
  }